import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import { isAllowedAttachmentURL } from '../lib/attachment-url';

/**
 * One tool call, as the DSH connector folded it.
 *
 * The body is computed by the connector from the tool's own presenter, so this
 * component never interprets a tool — it only lays out the card it was handed.
 * A body it cannot parse renders as nothing rather than throwing.
 *
 * The fields are read one at a time rather than through the connector's own
 * types, which live in a package this one does not depend on. The connector is
 * deployed separately and may be a different build. A value pulled out of the
 * call or the result is checked before it is drawn; a read inside the call or
 * the result that only picks which branch to draw takes a branch either way.
 */

interface Card {
  callId: string;
  name: string;
  status: 'running' | 'ok' | 'error';
  /** The label the card carries, when it carries one. */
  title?: string;
  call: Record<string, unknown> & { card: string };
  result?: Record<string, unknown> & { card: string };
  /**
   * Set when the connector cut something out of this card to send it. This
   * component reads it at the top level, and draws one notice from it.
   */
  truncated?: boolean;
}

/** Narrows to a value `typeof` calls an object, minus null and arrays. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The array a field holds, or an empty one when it holds anything else. */
function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? (value as unknown[]) : [];
}

/** Narrows to a half that carries a string `card` to switch on. */
function isCardHalf(value: unknown): value is Record<string, unknown> & { card: string } {
  return isRecord(value) && typeof value.card === 'string';
}

export function ToolCardMessage({ body }: { body: string }) {
  const card = useMemo<Card | null>(() => {
    try {
      const parsed = JSON.parse(body) as Card;
      return typeof parsed?.call?.card === 'string' ? parsed : null;
    } catch {
      return null;
    }
  }, [body]);

  if (!card) return null;

  // Both sources of the label can arrive as something other than a string. A
  // card left with neither still draws its status mark and its call.
  const name = typeof card.name === 'string' ? card.name : undefined;
  const label = typeof card.title === 'string' ? card.title : name;

  return (
    <div className={`tool-card tool-card-${card.status}`} data-tool={name}>
      <div className="tool-card-title">
        {card.status === 'running' ? '⋯' : card.status === 'error' ? '✗' : '✓'} {label}
      </div>
      <Call call={card.call} />
      {isCardHalf(card.result) && <Result result={card.result} />}
      {card.truncated === true && <div className="tool-card-truncated"><Trans>… (truncated)</Trans></div>}
    </div>
  );
}

function Call({ call }: { call: Record<string, unknown> & { card: string } }) {
  switch (call.card) {
    case 'terminal':
      return (
        <div className="tool-card-call">
          {typeof call.description === 'string' && <div>{call.description}</div>}
          {typeof call.cwd === 'string' && <div className="tool-card-cwd">{call.cwd}</div>}
        </div>
      );
    case 'diff':
      return <DiffList diffs={call.diffs} />;
    default:
      // `input` is the program a code-mode call carries, and the arguments of
      // anything else that chose to send them; `text` is the presenter's own
      // display blocks. Two slots, because they are two different things.
      return (
        <div className="tool-card-call">
          {typeof call.path === 'string' && <div className="tool-card-path">{call.path}</div>}
          {typeof call.input === 'string' && <pre className="tool-card-input">{call.input}</pre>}
          {typeof call.text === 'string' && <div>{call.text}</div>}
        </div>
      );
  }
}

function DiffList({ diffs }: { diffs: unknown }) {
  const { t } = useLingui();
  if (!Array.isArray(diffs)) return null;
  return (
    <ul className="tool-card-diffs">
      {asArray(diffs).map((d, i) =>
        isRecord(d) && typeof d.path === 'string' ? (
          <li key={i}>
            {d.path}
            {d.oldText === null ? t` (new)` : ''}
          </li>
        ) : null,
      )}
    </ul>
  );
}

function Result({ result }: { result: Record<string, unknown> & { card: string } }) {
  const { t } = useLingui();
  switch (result.card) {
    case 'terminal':
      return (
        <div className="tool-card-terminal">
          {typeof result.output === 'string' && <pre>{result.output}</pre>}
          <div className="tool-card-exit">
            <Trans>Exit code: {typeof result.exitCode === 'number' ? result.exitCode : '?'}</Trans>
            {typeof result.signal === 'string' ? ` (${result.signal})` : ''}
          </div>
        </div>
      );
    case 'read': {
      const lines: { number?: number; text: string }[] = [];
      for (const line of asArray(result.lines)) {
        if (isRecord(line) && typeof line.text === 'string') {
          lines.push({
            ...(typeof line.number === 'number' ? { number: line.number } : {}),
            text: line.text,
          });
        }
      }
      return (
        <div className="tool-card-read">
          <div className="tool-card-read-range">
            {typeof result.path === 'string' ? `${result.path} — ` : ''}
            {typeof result.totalLines === 'number'
              ? t`${lines.length}/${result.totalLines} lines`
              : <Plural value={lines.length} one="# line" other="# lines" />}
          </div>
          {lines.length > 0 && (
            <pre>
              {lines
                .map((l) => (l.number === undefined ? l.text : `${l.number}\t${l.text}`))
                .join('\n')}
            </pre>
          )}
          {typeof result.text === 'string' && <pre>{result.text}</pre>}
        </div>
      );
    }
    case 'search':
      return (
        <div className="tool-card-search">
          {typeof result.total === 'number' && (
            <div className="tool-card-search-total"><Plural value={result.total} one="# result" other="# results" /></div>
          )}
          {result.shape === 'paths' ? (
            <ul>
              {asArray(result.paths).map((p, i) =>
                typeof p === 'string' ? <li key={i}>{p}</li> : null,
              )}
            </ul>
          ) : (
            <ul>
              {asArray(result.files).map((f, i) =>
                isRecord(f) && typeof f.path === 'string' ? (
                  <li key={i}>
                    <div className="tool-card-path">{f.path}</div>
                    <ul>
                      {asArray(f.matches).map((m, j) =>
                        isRecord(m) && typeof m.line === 'string' ? (
                          <li key={j}>
                            {typeof m.lineNumber === 'number' ? `${m.lineNumber}: ` : ''}
                            {m.line}
                          </li>
                        ) : null,
                      )}
                    </ul>
                  </li>
                ) : null,
              )}
            </ul>
          )}
        </div>
      );
    case 'diff':
      return <DiffList diffs={result.diffs} />;
    case 'web':
      return (
        <div className="tool-card-web">
          {result.kind === 'fetch' ? (
            <div>
              {typeof result.url === 'string' ? result.url : ''}
              {typeof result.statusCode === 'number' ? ` — ${result.statusCode}` : ''}
            </div>
          ) : (
            <>
              {typeof result.answer === 'string' && <p>{result.answer}</p>}
              <ul>
                {asArray(result.sources).map((s, i) => {
                  if (!isRecord(s) || typeof s.url !== 'string') return null;
                  const url = s.url;
                  const label = typeof s.title === 'string' ? s.title : url;
                  // A search tool brings these URLs back off the open web, so
                  // they go through the allowlist the image path uses
                  // (`lib/attachment-url`): once trimmed, the value has to
                  // start with `http://` or `https://` in any letter case, or
                  // with a single `/`. A source that does not pass is drawn as
                  // text, not a link.
                  return (
                    <li key={i}>
                      {isAllowedAttachmentURL(url) ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {label}
                        </a>
                      ) : (
                        <span className="tool-card-source-unsafe">{label}</span>
                      )}
                      {typeof s.snippet === 'string' && <div>{s.snippet}</div>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      );
    default:
      return (
        <div className="tool-card-generic">
          {typeof result.text === 'string' && <pre>{result.text}</pre>}
        </div>
      );
  }
}
